import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, Divider, FormControlLabel, FormGroup, Switch, TextField, Typography } from '@mui/material';
import InfoText from 'components/shared/info-text.component';
import { ProfileLanguage, SUPPLIER_SERVICES, SupplierProfile, SupplierService } from 'models/organization-profile';
import ProfileLanguageSelect from './profile-language-select.component';
import { hasProfileText, initialProfileLanguage } from './profile-texts';

type SupplierProfileTabProps = {
    profile: SupplierProfile;
    onChange: (profile: SupplierProfile) => void;
    labLanguage: ProfileLanguage;
};

/**
 * DD-3662: how the lab presents itself to other labs — whether it is listed in the
 * supplier browser, the services it offers and a description per language.
 */
const SupplierProfileTab: FC<SupplierProfileTabProps> = ({ profile, onChange, labLanguage }) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const [language, setLanguage] = useState<ProfileLanguage>(() => initialProfileLanguage(profile.texts, labLanguage));
    const currentText = profile.texts.find((text) => text.language === language);
    const offersOther = profile.services.includes(SupplierService.OTHER);

    const toggleService = (service: SupplierService) =>
        onChange({
            ...profile,
            services: profile.services.includes(service)
                ? profile.services.filter((selected) => selected !== service)
                : [...profile.services, service],
        });

    const updateDescription = (description: string) =>
        onChange({
            ...profile,
            texts: [...profile.texts.filter((text) => text.language !== language), { language, description }],
        });

    return (
        <Box>
            <Box mb={2}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={profile.visible}
                            onChange={(_event, visible) => onChange({ ...profile, visible })}
                            data-test="supplier-profile.visible"
                        />
                    }
                    label={translate('lab-settings:SHOW_AS_SUPPLIER')}
                />
                <InfoText text={translate('lab-settings:SHOW_AS_SUPPLIER_INFO')} />
            </Box>
            {/* The rest only matters once the lab is listed; what was entered is kept. */}
            {profile.visible ? (
                <Fragment>
                    <Divider sx={{ mb: 3 }} />

                    <Box mb={3}>
                        <Typography variant="h3">{translate('lab-settings:SUPPLIER_SERVICES')}</Typography>
                        <InfoText text={translate('lab-settings:SUPPLIER_SERVICES_INFO')} />
                        {/* DD-3695: ten categories, each with what it covers. */}
                        <FormGroup
                            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 3 }}
                        >
                            {SUPPLIER_SERVICES.map((service) => (
                                <FormControlLabel
                                    key={service}
                                    sx={{ alignItems: 'flex-start', my: 0.5 }}
                                    control={
                                        <Checkbox
                                            sx={{ pt: 0.25 }}
                                            checked={profile.services.includes(service)}
                                            onChange={() => toggleService(service)}
                                            data-test={`supplier-profile.service-${service}`}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                {translate(`lab-settings:SUPPLIER_SERVICE_${service}`)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate(`lab-settings:SUPPLIER_SERVICE_${service}_INFO`)}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            ))}
                        </FormGroup>
                        {offersOther ? (
                            <Box mt={1} maxWidth={480}>
                                <TextField
                                    fullWidth
                                    label={translate('lab-settings:SUPPLIER_OTHER_SERVICE')}
                                    value={profile.otherService ?? ''}
                                    onChange={(event) => onChange({ ...profile, otherService: event.target.value })}
                                    inputProps={{ maxLength: 200, 'data-test': 'supplier-profile.other-service' }}
                                />
                            </Box>
                        ) : undefined}
                    </Box>
                    <Divider sx={{ mb: 3 }} />

                    <Typography variant="h3">{translate('lab-settings:SUPPLIER_DESCRIPTION')}</Typography>
                    <InfoText text={translate('lab-settings:SUPPLIER_DESCRIPTION_INFO')} />
                    <Box my={2}>
                        <ProfileLanguageSelect
                            value={language}
                            onChange={setLanguage}
                            writtenLanguages={profile.texts
                                .filter((text) => hasProfileText(text.description))
                                .map((text) => text.language)}
                            dataTest="supplier-profile.language"
                        />
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        label={translate('lab-settings:SUPPLIER_DESCRIPTION')}
                        value={currentText?.description ?? ''}
                        onChange={(event) => updateDescription(event.target.value)}
                        inputProps={{ maxLength: 4000, 'data-test': 'supplier-profile.description' }}
                    />
                </Fragment>
            ) : undefined}
        </Box>
    );
};

export default SupplierProfileTab;
